import { useMutation } from '@tanstack/react-query';
import { ShoppingBasket, BookOpen, Network } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { pushGtmEvent } from '@/Config/libs/analytics';
import { ActionDto, HttpActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { KnowledgeBaseActionDto } from '@/Domain/api/Action/dto/KnowledgeBaseAction.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';
import { HubSpotIcon } from '@/Domain/components/icons/HubSpot.icon';
import { ToolIcon } from '@/Domain/components/icons/Tool.icon';
import { ActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useActionLimit } from '@/Domain/hooks/Action/useActionLimit.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { isQuickchatEmail } from '@/Domain/utils/isQuickchatEmail';

type Props = {
  actions: ActionDto[];
};
export const ActionsCreateButton = ({ actions }: Props) => {
  const { createLimit: createdActionLimit } = useActionLimit();
  const { t } = useTranslation('actions_page');
  const { toggleModal } = useModalStore();
  const { user } = useUser();
  const { capture } = usePostHogAnalytics();

  const createKnowledgeBaseActionMutation = useMutation<KnowledgeBaseActionDto, unknown, void>({
    mutationKey: [QueryKey.actions.knowledgeBase.create],
  });
  const handleCreateKnowledgeBaseAction = async () => {
    const knowledgeBaseAction = await createKnowledgeBaseActionMutation.mutateAsync();
    capture(PostHogEventEnum.ActionCreated, { action_type: 'knowledge_base' });
    pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'knowledge_base' });
    toggleModal(MODAL_NAMES.KNOWLEDGE_BASE_ACTION_SHEET, { knowledgeBaseAction });
  };

  const createShopifyMCPActionMutation = useMutation<ShopifyMcpDto, unknown, void>({
    mutationKey: [QueryKey.actions.shopifyMcp.create],
  });
  const handleCreateShopifyMCPAction = async () => {
    const shopifyMcpAction = await createShopifyMCPActionMutation.mutateAsync();
    capture(PostHogEventEnum.ActionCreated, { action_type: 'shopify_mcp' });
    pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'shopify_mcp' });
    toggleModal(MODAL_NAMES.SHOPIFY_MCP_DIALOG, { shopifyMcpAction });
  };

  const createHttpRequestActionMutation = useMutation<HttpActionDto, unknown, void>({
    mutationKey: [QueryKey.actions.httpRequest.create],
  });

  const hasShopifyAction = (actions || []).find(
    (action) => action.type === ActionTypeEnum.ShopifyRemoteMcp,
  );

  const handleCreateRemoteMCPAction = async () => {
    capture(PostHogEventEnum.ActionCreated, { action_type: 'remote_mcp' });
    pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'remote_mcp' });
    toggleModal(MODAL_NAMES.REMOTE_MCP_DIALOG);
  };

  return (
    <TooltipProvider>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="add-action-button">
            <AddIcon />
            {t('button.add_action')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="flex flex-col space-y-0.5">
          <Tooltip open={(actions || []).length >= createdActionLimit ? undefined : false}>
            <TooltipTrigger>
              <DropdownMenuItem
                data-testid="create-http-request-action-button"
                onClick={() => {
                  toggleModal(MODAL_NAMES.CREATE_HTTP_REQUEST_ACTION_DIALOG);
                }}
                disabled={
                  (actions || []).length >= createdActionLimit ||
                  createHttpRequestActionMutation.isPending
                }
              >
                <ToolIcon />
                {t('button.add_http_request_action')}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent align="end">{t('toast.too_many_created_actions')}</TooltipContent>
          </Tooltip>

          <Tooltip open={(actions || []).length >= createdActionLimit ? undefined : false}>
            <TooltipTrigger>
              <DropdownMenuItem
                data-testid="create-hubspot-action-button"
                onClick={() => {
                  capture(PostHogEventEnum.ActionCreated, { action_type: 'hubspot_template' });
                  pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'hubspot_template' });
                  toggleModal(MODAL_NAMES.HUBSPOT_TEMPLATE_GALLERY_DIALOG);
                }}
                disabled={(actions || []).length >= createdActionLimit}
              >
                <HubSpotIcon className="size-4" fill="currentColor" />
                {t('button.add_hubspot_action')}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent align="end">{t('toast.too_many_created_actions')}</TooltipContent>
          </Tooltip>

          {isQuickchatEmail(user?.email || '') && (
            <Tooltip open={(actions || []).length >= createdActionLimit ? undefined : false}>
              <TooltipTrigger>
                <DropdownMenuItem
                  data-testid="create-knowledge-base-action-button"
                  onClick={handleCreateKnowledgeBaseAction}
                  disabled={
                    (actions || []).length >= createdActionLimit ||
                    createKnowledgeBaseActionMutation.isPending
                  }
                >
                  <BookOpen />
                  {t('button.add_knowledge_base_action')}
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent align="end">{t('toast.too_many_created_actions')}</TooltipContent>
            </Tooltip>
          )}

          <Tooltip open={hasShopifyAction ? undefined : false}>
            <TooltipTrigger>
              <DropdownMenuItem
                data-testid="create-shopify-mcp-action-button"
                onClick={handleCreateShopifyMCPAction}
                disabled={!!hasShopifyAction || createShopifyMCPActionMutation.isPending}
              >
                <ShoppingBasket />
                {t('button.add_shopify_mcp')}
              </DropdownMenuItem>
            </TooltipTrigger>
            <TooltipContent align="end">{t('tooltip.multiple_shopify_mcp')}</TooltipContent>
          </Tooltip>

          <Tooltip open={(actions || []).length >= createdActionLimit ? undefined : false}>
            <TooltipTrigger>
              <DropdownMenuItem
                data-testid="create-remote-mcp-action-button"
                onClick={handleCreateRemoteMCPAction}
                disabled={(actions || []).length >= createdActionLimit}
              >
                <Network />
                {t('button.add_remote_mcp')}
              </DropdownMenuItem>
            </TooltipTrigger>
          </Tooltip>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
};
